/**
 * Dragging a surface to scroll it.
 *
 * The kitchen display carries a note, inherited and never measured, saying the
 * screen "does not scroll by touch", and the up/down buttons exist because of
 * it. Nothing in this codebase blocks the gesture: every scrollable surface is
 * a plain `overflow: auto` pane, which any browser with touch scrolls by
 * itself. What the Atlantis till appears to do is report a finger as a MOUSE —
 * so a tap arrives as a click and works, and a finger dragged arrives as a
 * mouse dragged, which scrolls nothing.
 *
 * That is what this fills in: a mouse (or pen) dragged across a pane scrolls
 * it. Real touch is deliberately left alone — where the browser reports touch
 * it is already scrolling the pane natively, and moving it ourselves as well
 * would scroll it twice as fast.
 *
 * The buttons stay. They are still the only thing that works with a glove on,
 * and they are the guaranteed path if this panel turns out to report something
 * else again.
 *
 * Everything here is PURE: no React, no DOM, no clock.
 */

/**
 * How far the pointer must move before it counts as a drag rather than a tap.
 *
 * Below it a press is a press: the dish buttons inside a pane must keep working
 * when a finger lands slightly off-centre or shifts by a pixel or two.
 */
export const DRAG_THRESHOLD_PX = 8;

/** Pointer kinds we scroll ourselves. Touch already scrolls on its own. */
const DRAGGABLE_POINTERS = new Set(["mouse", "pen"]);

/**
 * Should this pointer going down begin a possible drag?
 *
 * Only the primary button, and only a pointer kind the browser is NOT already
 * scrolling with. A middle or right click is somebody else's business.
 */
export function startsDrag(pointerType: string, button: number): boolean {
  return button === 0 && DRAGGABLE_POINTERS.has(pointerType);
}

/**
 * Has the pointer moved far enough, and in the right direction, to be a drag?
 *
 * `along` is the movement on the axis this surface scrolls; `across` is the
 * movement on the other one. Two conditions, and the second is what keeps the
 * board still while a cook drags a queue: the movement must reach the
 * threshold, and it must be at least as large as the movement across. A finger
 * pulled straight down a station column scrolls that column and does NOT drag
 * the whole row of columns sideways with it.
 */
export function exceedsThreshold(along: number, across: number): boolean {
  return Math.abs(along) >= DRAG_THRESHOLD_PX && Math.abs(along) >= Math.abs(across);
}

/**
 * Where the surface should be scrolled to, given where the drag began.
 *
 * The content follows the finger, so the offset moves AGAINST the pointer, and
 * it is clamped: a drag that runs past the end of the content stops at the end
 * instead of storing up distance that has to be dragged back.
 */
export function nextOffset(
  offsetAtStart: number,
  positionAtStart: number,
  position: number,
  maxOffset: number,
): number {
  const moved = offsetAtStart - (position - positionAtStart);
  return Math.min(Math.max(moved, 0), Math.max(maxOffset, 0));
}
